// Date utilities for payroll management

/**
 * Get the last N months from current date
 * @param {number} count - Number of months to get (default: 6)
 * @returns {string[]} Array of year-month strings in format "YYYY-MM"
 */
export function getLastMonths(count = 6) {
  const months = [];
  const today = new Date();

  for (let i = 0; i < count; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
  }

  return months;
}

/**
 * Get the last 6 months (convenience function)
 * @returns {string[]} Array of year-month strings
 */
export function getLast6Months() {
  return getLastMonths(6);
}

/**
 * Format a year-month string to display format
 * @param {string} yearMonth - Year-month string in format "YYYY-MM"
 * @returns {string} Formatted month (e.g., "January 2024")
 */
export function formatMonth(yearMonth) {
  if (!yearMonth || !yearMonth.includes("-")) {
    return yearMonth || "Invalid Date";
  }

  const [year, month] = yearMonth.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);

  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/**
 * Get month name from month number
 * @param {number} month - Month number (1-12)
 * @returns {string} Month name
 */
export function getMonthName(month) {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long" });
}

/**
 * Get current year-month in format "YYYY-MM"
 * @returns {string} Current year-month
 */
export function getCurrentYearMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Parse year-month string to Date object
 * @param {string} yearMonth - Year-month string in format "YYYY-MM"
 * @returns {Date} Date object for the first day of the month
 */
export function parseYearMonth(yearMonth) {
  if (!yearMonth || !yearMonth.includes("-")) {
    return null;
  }

  const [year, month] = yearMonth.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, 1);
}

/**
 * Get previous month
 * @param {string} yearMonth - Current year-month in format "YYYY-MM"
 * @returns {string} Previous year-month
 */
export function getPreviousMonth(yearMonth) {
  const date = parseYearMonth(yearMonth);
  if (!date) return null;

  date.setMonth(date.getMonth() - 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Get next month
 * @param {string} yearMonth - Current year-month in format "YYYY-MM"
 * @returns {string} Next year-month
 */
export function getNextMonth(yearMonth) {
  const date = parseYearMonth(yearMonth);
  if (!date) return null;

  date.setMonth(date.getMonth() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Check if a year-month is valid
 * @param {string} yearMonth - Year-month string to validate
 * @returns {boolean} True if valid
 */
export function isValidYearMonth(yearMonth) {
  if (!yearMonth || typeof yearMonth !== "string") return false;

  const regex = /^\d{4}-(0[1-9]|1[0-2])$/;
  if (!regex.test(yearMonth)) return false;

  const [year, month] = yearMonth.split("-").map(Number);
  return year >= 2000 && year <= 2100 && month >= 1 && month <= 12;
}

/**
 * Compare two year-month strings
 * @param {string} a - First year-month
 * @param {string} b - Second year-month
 * @returns {number} -1 if a < b, 0 if a == b, 1 if a > b
 */
export function compareYearMonths(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Sort array of year-month strings in descending order (newest first)
 * @param {string[]} yearMonths - Array of year-month strings
 * @returns {string[]} Sorted array
 */
export function sortYearMonthsDesc(yearMonths) {
  return [...yearMonths].sort((a, b) => compareYearMonths(b, a));
}

/**
 * Sort array of year-month strings in ascending order (oldest first)
 * @param {string[]} yearMonths - Array of year-month strings
 * @returns {string[]} Sorted array
 */
export function sortYearMonthsAsc(yearMonths) {
  return [...yearMonths].sort(compareYearMonths);
}

/**
 * Format a date in Pacific Time with date only
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date in PT (e.g., "Jan 15, 2024")
 */
export function formatDateInPT(dateInput) {
  if (!dateInput) return "N/A";

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return "Invalid Date";

  return date.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a time in Pacific Time with time only
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted time in PT (e.g., "2:30 PM PT")
 */
export function formatTimeInPT(dateInput) {
  if (!dateInput) return "N/A";

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return "Invalid Date";

  return (
    date.toLocaleTimeString("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

/**
 * Format a date and time in Pacific Time
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date and time in PT (e.g., "Jan 15, 2024, 2:30 PM PT")
 */
export function formatDateTimeInPT(dateInput) {
  if (!dateInput) return "N/A";

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return "Invalid Date";

  return (
    date.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

/**
 * Check if a date is in Pacific Daylight Time (PDT)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {boolean} True if date is in PDT, false if PST
 */
export function isPacificDaylightTime(dateInput) {
  if (!dateInput) return false;

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return false;

  // Create a date in Pacific timezone and check if it's in DST
  const pacificDate = new Date(
    date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }),
  );
  const january = new Date(pacificDate.getFullYear(), 0, 1);
  const july = new Date(pacificDate.getFullYear(), 6, 1);

  // Check if standard time offset is Pacific Standard Time (UTC-8)
  const isStandardTime = january.getTimezoneOffset() > july.getTimezoneOffset();

  // In Northern Hemisphere, DST is in effect when standard time offset is greater
  return (
    isStandardTime &&
    pacificDate.getTimezoneOffset() < january.getTimezoneOffset()
  );
}

/**
 * Custom formatting in Pacific Time
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {boolean} includeTimeZoneSuffix - Whether to include "PT" suffix
 * @returns {string} Formatted date/time
 */
export function formatInPT(
  dateInput,
  options = {},
  includeTimeZoneSuffix = true,
) {
  if (!dateInput) return "N/A";

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return "Invalid Date";

  const formatted = date.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    ...options,
  });

  return includeTimeZoneSuffix ? formatted + " PT" : formatted;
}
